import type { EditorBlock } from '@/store/editorSlice';

/**
 * WEB-1854 (решение владельца 03.09, вариант А) — ЕДИНСТВЕННОЕ МЕСТО, ГДЕ
 * РЕШАЕТСЯ «КАКАЯ ПЛАТЁЖНАЯ КНОПКА НА ЭТОМ ЭКРАНЕ ГЛАВНАЯ».
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. На вопрос «главная кнопка» на экране оплаты
 * отвечали ДВЕ поверхности и отвечали по-разному:
 *   · липкая полоса покупки (resolveStickyPurchase, правило WEB-1706) —
 *     явное «Всегда показывать» → ПЕРВЫЙ такой блок, иначе ПОСЛЕДНИЙ
 *     платёжный блок;
 *   · «Цена по частям» (price_breakdown_text, WEB-1822) и юридический текст
 *     «Условия оплаты» (payment_terms) через ступень «своя цена платёжной
 *     кнопки экрана» (resolvePriceId, WEB-1302 Р2) — ПЕРВАЯ кнопка с годной
 *     ценой.
 * На экране без «Выбора тарифа», с двумя платёжными кнопками разной цены и
 * незаданной липкостью текст описывал цену ВЕРХНЕЙ кнопки, а полоса печатала
 * и списывала цену НИЖНЕЙ. Покупатель читал одну сумму, платил другую.
 *
 * ЛЕСТНИЦА (ратифицирована владельцем 03.09, вариант А):
 *   1. Есть видимый платёжный блок с явным `true` («Всегда показывать») —
 *      главный ПЕРВЫЙ такой по порядку на экране. Явное «да» автора не может
 *      быть проигнорировано, и главной становится именно помеченная кнопка.
 *   2. Явного `true` нет — главный ПОСЛЕДНИЙ платёжный блок (прежнее правило
 *      полосы, регресс-якорь WEB-1706).
 *   3. Платёжных блоков на экране нет — главной кнопки нет.
 * Явное `false` («Никогда не показывать») на выбор ГЛАВНОЙ кнопки не влияет:
 * оно гасит саму полосу, а не отменяет списание — эта ступень живёт у
 * `resolveStickyPurchase`, потому что относится к видимости полосы, а не к
 * вопросу «чью цену показывать».
 *
 * ПОБОЧНОЕ СЛЕДСТВИЕ, ПРИНЯТОЕ ВЛАДЕЛЬЦЕМ: у части опубликованных воронок
 * текст «Цены по частям» сменит сумму (там, где на экране две платёжные
 * кнопки с разными ценами). Задним числом ничего не правим.
 *
 * ⚠ ДЕНЬГИ. Возвращённый блок — источник и печатаемой суммы, и списания.
 * Победитель выбирается детерминированно по порядку блоков, и при ОДНОМ
 * платёжном блоке на экране правило вырождается ровно в прежнее поведение
 * обеих поверхностей: ни одна живая воронка с одной кнопкой оплаты не меняет
 * того, что списывает.
 *
 * ВТОРОЙ КОПИИ ЭТОГО ПРАВИЛА БЫТЬ НЕ ДОЛЖНО (правило WEB-1844 «два конца —
 * два сторожа»): двусторонний сторож
 * `__tests__/mainPayBlockSingleLadder.web1854.test.ts` обходит исходники
 * фронта и краснеет, если появится второе место, выбирающее платёжный блок
 * по позиции/флагу и не идущее через этот модуль.
 */

/**
 * Типы блоков, которые могут быть главной платёжной кнопкой экрана: они несут
 * настройку липкой полосы (`stickyPurchaseButton`, WEB-1030) и рисуются как
 * кнопка «Оплатить» (оба идут через BuyNowButton).
 *
 * Экспресс-кошельки (`stripe_wallets_express`) в перечень НЕ входят
 * намеренно: полосу они вести не могут (настройки у них нет, а полоса
 * дублирует именно кнопку), поэтому «главной кнопкой» их назвать нельзя без
 * смены владельца полосы у живых воронок — это за границей решения владельца
 * от 03.09. Их собственная цена по-прежнему участвует в лестнице цены
 * запасной ступенью (resolvePriceId.resolveScreenPayButtonPriceId, WEB-1302).
 */
export function isPayBlock(block: EditorBlock): boolean {
  return block.type === 'button_to_pay' || block.type === 'stripe_checkout_form';
}

/**
 * Платёжные блоки экрана в порядке экрана, без скрытых правилами видимости.
 *
 * Скрытый блок не должен ни включать полосу, ни диктовать цену (WEB-1113):
 * `PaywallScreenWizard` передаёт сюда уже отфильтрованные `visibleBlocks`, а
 * `hiddenBlockIds` — второй, независимый заслон для вызывающих, у которых на
 * руках сырой состав экрана.
 */
export function screenPayBlocks(
  blocks: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): EditorBlock[] {
  return blocks.filter((b) => isPayBlock(b) && !hiddenBlockIds?.has(b.id));
}

/**
 * Главная платёжная кнопка экрана — ОДНА лестница на все поверхности.
 * `undefined` только когда платёжных блоков на экране нет.
 */
export function resolveMainPayBlock(
  blocks: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): EditorBlock | undefined {
  const payBlocks = screenPayBlocks(blocks, hiddenBlockIds);
  if (payBlocks.length === 0) return undefined;
  const optedIn = payBlocks.find((b) => b.stickyPurchaseButton === true);
  return optedIn ?? payBlocks[payBlocks.length - 1];
}
